import {
  LBS_TO_TONNES,
  BARLEY_COST_FROM_TONNE_TO_BUSHEL,
  BARLEY_COST_PER_BUSHEL_TO_SILAGE,
} from '../../constants'
import {addDaysToDate} from '../math'
import {feedValuesClosestToDate} from './sharedCalculations'

export const hedgeablePrice = (
  defaults,
  finishing,
  dtnFeedData,
  basisValues,
) => {
  return finishingProjectedPrice(defaults, finishing, dtnFeedData, basisValues)
}

export const profitLoss = (defaults, finishing, dtnFeedData, basisValues) => {
  const projectedPrice = finishingProjectedPrice(
    defaults,
    finishing,
    dtnFeedData,
    basisValues,
  )
  const totalCostPerHead = breakevenTotalCostPerHead(defaults, finishing)
  return Number(defaults.outWeight) / 100 * projectedPrice - totalCostPerHead
}

export const breakevenPP = (defaults, finishing, dtnFeedData, basisValues) => {
  const projectedPrice = finishingProjectedPrice(
    defaults,
    finishing,
    dtnFeedData,
    basisValues,
  )
  const productionCostPerHead = finishingProductionCostPerHead(
    defaults,
    finishing,
  )
  return (
    (Number(defaults.outWeight) / 100 * projectedPrice -
      productionCostPerHead) /
    Number(finishing.inWeight)
  )
}

export const breakevenBasis = (
  defaults,
  finishing,
  dtnFeedData,
  basisValues,
) => {
  const saleDate = finishingSaleDate(defaults, finishing)
  const {liveCattle, cdn} = feedValuesClosestToDate(
    saleDate,
    dtnFeedData,
    basisValues,
  )
  const pricePerCWT = breakevenPricePerCWT(defaults, finishing)
  return -1 * (liveCattle.lastValue / cdn.lastValue - pricePerCWT)
}

export const breakevenCDN = (defaults, finishing, dtnFeedData, basisValues) => {
  const saleDate = finishingSaleDate(defaults, finishing)
  const {liveCattle, basisValue} = feedValuesClosestToDate(
    saleDate,
    dtnFeedData,
    basisValues,
  )
  const pricePerCWT = breakevenPricePerCWT(defaults, finishing)
  return (
    Number(liveCattle.lastValue) /
    (Number(pricePerCWT) - Number(basisValue.val))
  )
}

export const breakevenFutures = (
  defaults,
  finishing,
  dtnFeedData,
  basisValues,
) => {
  const saleDate = finishingSaleDate(defaults, finishing)
  const {cdn, basisValue} = feedValuesClosestToDate(
    saleDate,
    dtnFeedData,
    basisValues,
  )
  const pricePerCWT = breakevenPricePerCWT(defaults, finishing)
  return (Number(pricePerCWT) - Number(basisValue.val)) * Number(cdn.lastValue)
}

export const costOfGainPerCWT = (
  defaults,
  finishing,
  dtnFeedData,
  basisValues,
) => {
  const productionCostPerHead = finishingProductionCostPerHead(
    defaults,
    finishing,
  )
  return (
    Number(productionCostPerHead) /
    (Number(defaults.outWeight) - Number(finishing.inWeight))
  )
}

export const breakevenPricePerCWT = (defaults, finishing) => {
  const totalCostPerHead = breakevenTotalCostPerHead(defaults, finishing)
  return Number(totalCostPerHead) / Number(defaults.outWeight) * 100
}

export const breakevenTotalCostPerHead = (defaults, finishing) => {
  return (
    finishingProductionCostPerHead(defaults, finishing) +
    Number(finishing.purchasePricePerLb) * Number(finishing.inWeight)
  )
}

export const finishingProductionCostPerHead = (defaults, finishing) => {
  const totalCostOfFeedPerHead = finishingTotalCostPerHead(defaults, finishing)
  const daysOnFeed = finishingDaysOnFeed(defaults, finishing)

  return (
    totalCostOfFeedPerHead +
    Number(defaults.truckingInPerHead) +
    Number(defaults.marketingPerHead) +
    Number(defaults.yardagePerDay) * daysOnFeed +
    Number(defaults.vetAndMedPerHead) +
    Number(defaults.deathLossPerHead) +
    Number(defaults.beddingPerHead) +
    Number(defaults.interestPerHead)
  )
}

export const finishingTotalCostPerHead = (defaults, finishing) => {
  const barleyCostPerHead = finishingBarleyCostPerHead(defaults, finishing)
  const supplementCostPerHead = finishingSupplementCostPerHead(
    defaults,
    finishing,
  )
  const silagePerHead = finishingSilagePerHead(defaults, finishing)
  return (
    Number(barleyCostPerHead) +
    Number(supplementCostPerHead) +
    Number(silagePerHead)
  )
}

export const finishingSilagePerHead = (defaults, finishing) => {
  const silageCost = finishingSilageCost(defaults)
  return (
    Number(defaults.silage) /
    LBS_TO_TONNES *
    Number(silageCost) *
    finishingDaysOnFeed(defaults, finishing)
  )
}

export const finishingSilageCost = (defaults) => {
  if (defaults.silageCost != null) {
    return defaults.silageCost
  }
  const barleyCostPerBushel =
    Number(defaults.barleyCost) / BARLEY_COST_FROM_TONNE_TO_BUSHEL
  return barleyCostPerBushel * BARLEY_COST_PER_BUSHEL_TO_SILAGE
}

export const finishingSupplementCostPerHead = (defaults, finishing) => {
  return (
    Number(defaults.supplement) /
    LBS_TO_TONNES *
    Number(defaults.supplementCost) *
    finishingDaysOnFeed(defaults, finishing)
  )
}

export const finishingBarleyCostPerHead = (defaults, finishing) => {
  return (
    Number(defaults.barley) /
    LBS_TO_TONNES *
    Number(defaults.barleyCost) *
    finishingDaysOnFeed(defaults, finishing)
  )
}

const finishingDaysOnFeed = (defaults, finishing) => {
  return Math.floor(
    (Number(defaults.outWeight) - Number(finishing.inWeight)) /
      Number(defaults.avgDailyGain),
  )
}

const finishingProjectedPrice = (
  defaults,
  finishing,
  dtnFeedData,
  basisValues,
) => {
  const finishDate = finishingSaleDate(defaults, finishing)
  const {liveCattle, cdn, basisValue} = feedValuesClosestToDate(
    finishDate,
    dtnFeedData,
    basisValues,
  )

  return (
    Number(liveCattle.lastValue) / Number(cdn.lastValue) +
    Number(basisValue.val)
  )
}

export const finishingSaleDate = (defaults, finishing) => {
  return addDaysToDate(
    finishing.purchaseDate,
    finishingDaysOnFeed(defaults, finishing),
  )
}



// WEBPACK FOOTER //
// ./src/helpers/calculators/finishingCalculations.js